import {
    BookingEngineField,
    DataTestId,
    PORTAL_CONTAINER_ID,
    SUGGESTED_CURRENCY_CODES,
    useIsMobile,
    TextKey,
} from '@mews-distributor/common';
import { CurrencySelect } from '@mews/business-components';
import { useIsTextKey, useLocalizeText } from '@mews/framework';
import { CurrencySelectDialogButton } from '@mews/gx-ui';
import { Appbar as BaseAppbar, Container, IconButton, IconName, Stack } from '@optimus-web/core';

interface Currency {
    code: string;
    symbol: string;
}

const appbarFactory =
    ({ Logo, LanguageSelector, LanguageSelectDialog }: $TSFixMe = {}) =>
    ({
        currencies,
        currency: selectedCurrency,
        language,
        languages,
        onClose,
        onCurrencyChange,
        onLanguageChange,
        propertyImageId,
        propertyPaymentGatewayDefaultCurrencyCode,
        propertyName,
        showCloseButton,
        showCurrencySelect,
        showPropertyInfo,
        isFxMulticurrencyServiceEnabled,
        ...other
    }: {
        currencies: Currency[];
        currency: string;
        language: string;
        languages: string[];
        onClose: () => void;
        onCurrencyChange: (currency: string) => void;
        onLanguageChange: (language: string) => void;
        propertyImageId: string;
        propertyPaymentGatewayDefaultCurrencyCode: string;
        propertyName: string;
        showCloseButton: boolean;
        showCurrencySelect: boolean;
        showPropertyInfo: boolean;
        isFxMulticurrencyServiceEnabled: boolean;
    }) => {
        const lt = useLocalizeText();
        const isMobile = useIsMobile();
        const isTextKey = useIsTextKey();

        const currenciesWithDescriptionAdded = currencies.map((currency) => {
            const currencyTextKey = `Currency${currency.code}`;

            return {
                code: currency.code,
                symbol: currency.symbol,
                description: isTextKey(currencyTextKey) ? lt({ textKey: currencyTextKey }) : null,
            };
        });

        return (
            <BaseAppbar
                hasBorder
                leftSide={
                    Logo != null && showPropertyInfo ? (
                        <Container phoneOnly={{ padding: '0' }} tabletUp={{ padding: ['0', '200'] }}>
                            <Logo
                                key="logo"
                                propertyName={propertyName}
                                imageProps={{ id: propertyImageId }}
                                isMobile={isMobile}
                            />
                        </Container>
                    ) : null
                }
                rightSide={
                    <Container phoneOnly={{ padding: '0' }} tabletUp={{ padding: ['0', '200'] }}>
                        <Stack spacing="200" itemAlignment="center">
                            {isFxMulticurrencyServiceEnabled ? (
                                <>
                                    {LanguageSelectDialog != null && (
                                        <LanguageSelectDialog
                                            key="languageSelector"
                                            languages={languages}
                                            selectedLanguageCode={language}
                                            onChange={onLanguageChange}
                                            dataTestId={DataTestId.LanguageSelector}
                                            portalContainerId={PORTAL_CONTAINER_ID}
                                            buttonAriaLabelText={lt({ textKey: TextKey.SelectYourLanguage })}
                                            dialogTitleText={lt({ textKey: TextKey.SelectYourLanguage })}
                                        />
                                    )}

                                    {showCurrencySelect && (
                                        <CurrencySelectDialogButton
                                            key="currencySelect"
                                            currencies={currenciesWithDescriptionAdded}
                                            onChange={onCurrencyChange}
                                            selectedCurrencyCode={selectedCurrency}
                                            dataTestId={DataTestId.CurrencySelector}
                                            portalContainerId={PORTAL_CONTAINER_ID}
                                            suggestedCurrencyCodes={SUGGESTED_CURRENCY_CODES}
                                            propertyCurrencyCode={propertyPaymentGatewayDefaultCurrencyCode}
                                            propertyCurrencyLabelText={lt({ textKey: TextKey.Property })}
                                            dialogTitleText={lt({ textKey: TextKey.SelectYourCurrency })}
                                            buttonAriaLabelText={lt({ textKey: TextKey.SelectYourCurrency })}
                                            suggestedCurrenciesTitleText={lt({
                                                textKey: TextKey.CurrencySelectDialogTitleSuggestedCurrencies,
                                            })}
                                            otherCurrenciesTitleText={lt({
                                                textKey: TextKey.CurrencySelectDialogTitleAllCurrencies,
                                            })}
                                        />
                                    )}
                                </>
                            ) : (
                                <>
                                    {LanguageSelector != null && (
                                        <LanguageSelector
                                            key="languageSelector"
                                            languages={languages}
                                            name={BookingEngineField.LanguageSelector}
                                            selected={language}
                                            onChange={onLanguageChange}
                                            dataTestId={DataTestId.LanguageSelector}
                                            portalContainerId={PORTAL_CONTAINER_ID}
                                        />
                                    )}

                                    {showCurrencySelect && (
                                        <CurrencySelect
                                            key="currencySelect"
                                            currencies={currencies}
                                            onChange={onCurrencyChange}
                                            selected={selectedCurrency}
                                            dataTestId={DataTestId.CurrencySelector}
                                            name={BookingEngineField.CurrencySelect}
                                            portalContainerId={PORTAL_CONTAINER_ID}
                                        />
                                    )}
                                </>
                            )}
                        </Stack>
                    </Container>
                }
                rightActions={
                    showCloseButton ? (
                        <IconButton
                            key="closeButton"
                            icon={IconName.CrossClose}
                            onClick={onClose}
                            variant="ghost"
                            accessibleText={lt({ textKey: TextKey.Close })}
                            type="submit"
                        />
                    ) : null
                }
                {...other}
            />
        );
    };

export { appbarFactory };
export const Appbar = appbarFactory();
